// Regeln (Basis-Separation mit Presets, Regelbaum) und Analyse (Recap, Song-Turnover, Sendenachweis).

import { useState } from "react";
import { api, toast, useLoad } from "../../api";
import { Empty, Field, Modal, Switch, Tip, useMe } from "../../ui";

const KINDS: [string, string][] = [
  ["artist_sep", "Interpreten-Separation"], ["song_sep", "Song-Separation"], ["attr_sep", "Attribut-Separation"],
  ["max_in_row", "Max. in Folge"], ["daypart_block", "Tageszeit-Sperre"],
];
const ATTRS: [string, string][] = [
  ["genre", "Genre"], ["category", "Unterkategorie"], ["mood", "Stimmung"], ["era", "Ära"], ["energy", "Energie"],
  ["soundcode", "Soundcode"], ["voice_gender", "Stimmgeschlecht"], ["texture", "Textur"], ["tempo", "Tempo"], ["end_type", "Endtyp"],
];
const PRIOS: [string, string][] = [["normal", "Normal"], ["important", "Wichtig"], ["high", "Hoch"], ["critical", "Kritisch (nie lockern)"]];

function describe(r: any) {
  const a = ATTRS.find(([k]) => k === r.attribute)?.[1] ?? r.attribute;
  switch (r.kind) {
    case "artist_sep": return `Gleicher Interpret frühestens nach ${r.minutes} min`;
    case "song_sep": return `Gleicher Song frühestens nach ${r.minutes} min`;
    case "attr_sep": return `Gleiche(s) ${a} frühestens nach ${r.minutes} min`;
    case "max_in_row": return `Höchstens ${r.max_count} × gleiche(s) ${a}${r.value ? ` „${r.value}“` : ""} hintereinander`;
    case "daypart_block": return `${a} „${r.value}“ gesperrt ${r.hours?.from ?? 0}–${r.hours?.to ?? 24} Uhr`;
    default: return r.kind;
  }
}

export function Rules() {
  const me = useMe();
  const canEdit = ["admin", "programm"].includes(me?.role);
  const { data: rules, reload } = useLoad<any[]>("/api/rules");
  const { data: rot, reload: reloadRot } = useLoad<any>("/api/rotation/settings");
  const [add, setAdd] = useState(false);
  const [v, setV] = useState<any>({ kind: "attr_sep", attribute: "genre", minutes: 30, max_count: 2, priority: "normal", breakable: true, hours: { from: 6, to: 10 }, value: "" });
  const hint = () => toast("Regeln geändert – sie gelten für neu geplante Stunden. Im Sendeplan: Mehr ▾ › Neu aufbauen.");
  const put = async (r: any, patch: any) => {
    await api(`/api/rules/${r.id}`, { method: "PUT", body: patch });
    reload();
    hint();
  };
  const move = async (r: any, dir: number) => {
    const list = [...(rules ?? [])];
    const i = list.findIndex((x) => x.id === r.id);
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    for (const [k, x] of list.entries()) await api(`/api/rules/${x.id}`, { method: "PUT", body: { sort: k } });
    reload();
  };
  return (
    <div className="stack">
      <div className="card">
        <h2>Basis-Separation</h2>
        {rot && (
          <div className="form">
            <Field label={`Song-Ruhezeit: ${rot.song_rest_min} min`}>
              <input type="range" min={15} max={720} step={15} disabled={!canEdit} defaultValue={rot.song_rest_min} onMouseUp={async (e) => { await api("/api/rotation/settings", { method: "PUT", body: { song_rest_min: Number((e.target as HTMLInputElement).value) } }); reloadRot(); }} />
            </Field>
            <Field label={`Interpreten-Ruhezeit: ${rot.artist_rest_min} min`}>
              <input type="range" min={0} max={360} step={5} disabled={!canEdit} defaultValue={rot.artist_rest_min} onMouseUp={async (e) => { await api("/api/rotation/settings", { method: "PUT", body: { artist_rest_min: Number((e.target as HTMLInputElement).value) } }); reloadRot(); }} />
            </Field>
            <Field label="Presets" full>
              <div className="row">
                {[["eng", "Eng (240/90)"], ["ausgewogen", "Ausgewogen (180/60)"], ["locker", "Locker (90/30)"]].map(([k, l]) => (
                  <button key={k} className="btn small" disabled={!canEdit} onClick={async () => { await api("/api/rules/preset", { body: { preset: k } }); toast(`Preset ${l} – gilt für alle Musik-Kategorien`); reloadRot(); }}>{l}</button>
                ))}
              </div>
            </Field>
            <Field label="Scored Rotation" full>
              <Switch checked={rot.scored} onChange={async (x) => { if (!canEdit) return; await api("/api/rotation/settings", { method: "PUT", body: { scored: x } }); reloadRot(); }} label={rot.scored ? "an (Punktwertung)" : "aus (am längsten ungespielt zuerst)"} />
            </Field>
          </div>
        )}
        <p className="small muted">Die Ruhezeiten je Kategorie (Kategorien & Rotation) gehen vor. Separation gilt über Stundengrenzen und gegen den geplanten Sendeplan.</p>
      </div>
      <div className="card">
        <div className="row" style={{ marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>Regelbaum</h2>
          <span className="spacer" />
          {canEdit && <button className="btn primary" onClick={() => setAdd(true)}>+ Regel</button>}
        </div>
        <Tip>Brechbare Regeln werden gelockert, wenn sonst kein Song übrig bleibt – von „Normal“ aufwärts. „Kritisch“ und unbrechbare Regeln werden nie gelockert; dann bleibt der Slot im Sendeplan offen.</Tip>
        {!rules?.length ? <Empty>Keine zusätzlichen Regeln.</Empty> : (
          <table className="list">
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} style={{ opacity: r.enabled ? 1 : 0.5 }}>
                  <td>{canEdit && <><button className="iconbtn" onClick={() => move(r, -1)}>↑</button><button className="iconbtn" onClick={() => move(r, 1)}>↓</button></>}</td>
                  <td>{describe(r)}</td>
                  <td><span className={`badge ${r.breakable ? "" : "bad"}`}>{r.breakable ? "brechbar" : "unbrechbar"}</span></td>
                  <td>
                    <select style={{ width: 170 }} disabled={!canEdit} value={r.priority} onChange={(e) => put(r, { priority: e.target.value })}>{PRIOS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                  </td>
                  <td><Switch checked={r.enabled} onChange={(x) => canEdit && put(r, { enabled: x })} /></td>
                  <td>{canEdit && <button className="btn small danger" onClick={async () => { await api(`/api/rules/${r.id}`, { method: "DELETE" }); reload(); }}>✕</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {add && (
        <Modal title="Neue Regel" onClose={() => setAdd(false)}>
          <div className="form">
            <Field label="Art"><select value={v.kind} onChange={(e) => setV({ ...v, kind: e.target.value })}>{KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
            {["attr_sep", "max_in_row", "daypart_block"].includes(v.kind) && (
              <Field label="Attribut"><select value={v.attribute} onChange={(e) => setV({ ...v, attribute: e.target.value })}>{ATTRS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
            )}
            {["artist_sep", "song_sep", "attr_sep"].includes(v.kind) && <Field label="Minuten"><input type="number" min={1} value={v.minutes} onChange={(e) => setV({ ...v, minutes: Number(e.target.value) })} /></Field>}
            {v.kind === "max_in_row" && <Field label="Höchstens in Folge"><input type="number" min={1} value={v.max_count} onChange={(e) => setV({ ...v, max_count: Number(e.target.value) })} /></Field>}
            {["max_in_row", "daypart_block"].includes(v.kind) && <Field label={v.kind === "daypart_block" ? "Wert" : "nur für Wert (optional)"}><input type="text" value={v.value} onChange={(e) => setV({ ...v, value: e.target.value })} /></Field>}
            {v.kind === "daypart_block" && (
              <Field label="Gesperrt von – bis (Uhr)">
                <div className="row">
                  <input type="number" min={0} max={23} value={v.hours.from} onChange={(e) => setV({ ...v, hours: { ...v.hours, from: Number(e.target.value) } })} />
                  <input type="number" min={1} max={24} value={v.hours.to} onChange={(e) => setV({ ...v, hours: { ...v.hours, to: Number(e.target.value) } })} />
                </div>
              </Field>
            )}
            <Field label="Priorität"><select value={v.priority} onChange={(e) => setV({ ...v, priority: e.target.value })}>{PRIOS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
            <Field label="Brechbar"><Switch checked={v.breakable} onChange={(x) => setV({ ...v, breakable: x })} label={v.breakable ? "darf gelockert werden" : "nie lockern"} /></Field>
          </div>
          <div className="row" style={{ marginTop: 12 }}><span className="spacer" /><button className="btn primary" onClick={async () => { try { await api("/api/rules", { body: v }); setAdd(false); reload(); hint(); } catch (e: any) { toast(e.message, true); } }}>Anlegen</button></div>
        </Modal>
      )}
    </div>
  );
}

export function Analysis() {
  const [days, setDays] = useState(7);
  const { data: recap } = useLoad<any>(`/api/analysis/recap?days=${days}`, [days]);
  const { data: turnover } = useLoad<any[]>(`/api/analysis/turnover?days=${days}`, [days]);
  const today = new Date().toISOString().slice(0, 10);
  const from = new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
  return (
    <div className="stack">
      <div className="row">
        <select style={{ width: 160 }} value={days} onChange={(e) => setDays(Number(e.target.value))}>{[1, 7, 14, 31].map((d) => <option key={d} value={d}>letzte {d} {d === 1 ? "Tag" : "Tage"}</option>)}</select>
        <span className="spacer" />
        <a className="btn" href={`/api/playlog?format=csv&limit=5000&from=${from}T00:00:00&to=${today}T23:59:59`}>⇩ As-aired-Report (CSV)</a>
      </div>
      {recap && (
        <div className="grid g3">
          <div className="card kpi"><span className="v" style={{ color: "var(--ok)" }}>{recap.clean_pct} %</span><span className="l">sauber geplant ({recap.slots} Musik-Slots)</span></div>
          <div className="card kpi"><span className="v" style={{ color: "var(--warn)" }}>{recap.relaxed_pct} %</span><span className="l">mit gelockerten Regeln</span></div>
          <div className="card kpi"><span className="v" style={{ color: "var(--bad)" }}>{recap.gaps_pct} %</span><span className="l">Lücken (nicht gefüllt)</span></div>
        </div>
      )}
      <div className="grid g2">
        <div className="card">
          <h2>Meist gebrochene Regeln</h2>
          {!recap?.broken?.length ? <Empty>Keine.</Empty> : recap.broken.map((b: any) => <div key={b.rule} className="row"><span>{b.rule}</span><span className="spacer" /><b>{b.n}×</b></div>)}
        </div>
        <div className="card">
          <h2>Kategorien-Mix gegen Ziel</h2>
          {(recap?.mix ?? []).map((m: any) => (
            <div key={m.category} style={{ marginBottom: 6 }}>
              <div className="row small"><span>{m.category}</span><span className="spacer" /><span>{m.pct} %{m.target != null ? ` / Ziel ${m.target} %` : ""}</span></div>
              <div className="progress"><div style={{ width: `${Math.min(100, m.pct)}%`, background: m.target != null && Math.abs(m.pct - m.target) > 5 ? "var(--warn)" : "var(--ok)" }} /></div>
            </div>
          ))}
        </div>
      </div>
      <div className="card">
        <h2>Song-Turnover</h2>
        <table className="list">
          <thead><tr><th>Kategorie</th><th>Songs</th><th>Spins</th><th>Umläufe pro Tag</th><th>Ø Stunden zwischen Spins eines Songs</th></tr></thead>
          <tbody>{(turnover ?? []).map((t) => <tr key={t.category}><td>{t.category}</td><td>{t.songs}</td><td>{t.plays}</td><td>{t.turns_per_day}</td><td>{t.hours_between ?? "–"}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

/** Public, read-only view of a shared log (no login). */
export function SharedLog({ token }: { token: string }) {
  const { data, error } = useLoad<any>(`/api/public/share/${token}`);
  return (
    <div style={{ maxWidth: 900, margin: "24px auto", padding: "0 16px" }}>
      <div className="brand" style={{ color: "var(--ink)", padding: 0, marginBottom: 12 }}><span className="dot">●</span> Radiocast</div>
      {error && <div className="banner bad">{error}</div>}
      {data?.entry && (
        <div className="card">
          <h2>{data.station}: {data.entry.artist} – {data.entry.title}</h2>
          <div className="muted small">{new Date(data.entry.planned_start).toLocaleString("de-DE")}</div>
          {data.entry.audio && <audio controls src={`/api/public/share/${token}/audio`} style={{ width: "100%", marginTop: 10 }} />}
        </div>
      )}
      {data?.entries && (
        <div className="card">
          <h2>{data.station} – Sendeplan {String(data.day_from).slice(0, 10)}{data.day_to !== data.day_from ? ` bis ${String(data.day_to).slice(0, 10)}` : ""}</h2>
          <table className="list">
            <tbody>
              {data.entries.map((e: any, i: number) => (
                <tr key={i}><td className="mono">{new Date(e.planned_start).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</td><td>{e.title ? `${e.artist} – ${e.title}` : e.label}</td><td className="small muted">{e.category}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && <div className="small muted" style={{ marginTop: 8 }}>Nur-Lese-Link, gültig bis {new Date(data.expires_at).toLocaleDateString("de-DE")}.</div>}
    </div>
  );
}
